import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { enterModal, exitModal } from 'actions/modal';
import { shouldFetchMatrix } from 'constants/matrices';
import { fetchMatrix } from 'actions/matrices';
import MatrixDeletePopoverContent from 'components/matrices/MatrixDeletePopoverContent';
import './MatrixDeletePopoverWrapper.scss';
import { writePermissions } from 'constants/auth';
import MiniLoader from 'components/MiniLoader';

// NB: matrixId may be for a matrix or an experiment
const MatrixDeletePopoverWrapper = ({ matrix, onDelete, onCancel }) => {
  const dispatch = useDispatch();
  const auth = useSelector(state => state.auth);
  const userId = useMemo(() => auth.user?.claims?.user_id ?? null, [auth.user]);
  const matricesLoading = useSelector(state => state.matrices._loading);
  const matricesLoaded = useSelector(state => state.matrices._loaded);

  const allMatricesAndExperiments = useSelector(
    state => state.matrices.matrices
  ).filter(({ savedByUser }) => savedByUser);

  const experiments = useMemo(
    () => allMatricesAndExperiments.filter(({ parentId }) => parentId !== null),
    [allMatricesAndExperiments]
  );

  const userExperiments = useMemo(() => {
    if (!userId) return [];
    return experiments.filter(({ roles }) =>
      writePermissions.includes(roles[userId])
    );
  }, [experiments, userId]);

  const children = useMemo(() => {
    return userExperiments.filter(item => item?.parentId === matrix.id);
  }, [userExperiments, matrix]);

  useEffect(() => {
    // Make sure we fetch matrices before deleting as
    // they are needed for that function to run
    if (shouldFetchMatrix(matrix.id, matrix, matricesLoaded, matricesLoading)) {
      dispatch(fetchMatrix(matrix.id));
    }
    for (const child of children) {
      if (shouldFetchMatrix(child.id, child, matricesLoaded, matricesLoading)) {
        dispatch(fetchMatrix(child.id));
      }
    }
  }, [dispatch, matricesLoaded, matricesLoading, matrix, children]);

  useEffect(() => {
    dispatch(enterModal());

    return () => {
      dispatch(exitModal());
    };
  }, [dispatch]);

  const isLoaded = useMemo(() => {
    const allRelevantMatrices = children.map(c => c.id).concat([matrix.id]);
    return allRelevantMatrices.every(id => !!matricesLoaded[id]);
  }, [matricesLoaded, matrix, children]);

  return (
    <div className="MatrixDeletePopoverWrapper">
      {isLoaded ? (
        <MatrixDeletePopoverContent
          matrix={matrix}
          children={children}
          onDelete={onDelete}
          onCancel={onCancel}
        />
      ) : (
        <div className="MatrixDeletePopoverWrapper-loader-container">
          <div className="MatrixDeletePopoverWrapper-loader">
            <MiniLoader />
          </div>
        </div>
      )}
    </div>
  );
};

export default MatrixDeletePopoverWrapper;
